import type { MutableRefObject } from 'react';

import { TableSharedCssClassName } from '@atlaskit/editor-common/styles';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import type { DeletedDiffWidget } from '@atlaskit/editor-plugin-show-diff/show-diff-plugin-type';
import type { Schema } from '@atlaskit/editor-prosemirror/model';
import { findDomRefAtPos, findParentNodeClosestToPos } from '@atlaskit/editor-prosemirror/utils';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { InvokedFrom } from '../../utils/analytics/analyticsFlowTypes';
import { isPromptUpdatesEnabledNoExposure } from '../../utils/prompt-updates-enabled';

/** Width (rem) removed from the AI modal in the New AIFC Editor Experience. */
export const MODAL_WIDTH_REDUCTION_REM: number = 10;

/** Right shift (rem) to re-centre the narrower modal: half the removed width. */
export const MODAL_LEFT_OFFSET_REM: number = MODAL_WIDTH_REDUCTION_REM / 2;

/**
 * The 10rem AIFC inset is a full-page visual treatment. Comment and chromeless
 * hosts are already as wide as their popup, so subtracting 10rem crushes the
 * prompt and the new agent selector.
 */
export function shouldNarrowAifcModal(appearance?: EditorAppearance): boolean {
	return appearance !== 'comment' && appearance !== 'chromeless';
}

/** Legacy modal width: editor content plus the 2rem bleed. */
export const LEGACY_MODAL_WIDTH = 'calc(var(--content-width) + 2rem)';

/** Narrower AIFC modal width used on full-page surfaces only. */
export const NEW_AIFC_MODAL_WIDTH: string = `calc(var(--content-width) - ${MODAL_WIDTH_REDUCTION_REM}rem)`;

/**
 * Width CSS for the AI modal. The AIFC inset applies only when the prompt
 * updates are on *and* the host is wide enough to spare 10rem.
 */
export function getAifcModalWidth(appearance?: EditorAppearance): string {
	if (isPromptUpdatesEnabledNoExposure() && shouldNarrowAifcModal(appearance)) {
		return NEW_AIFC_MODAL_WIDTH;
	}
	return LEGACY_MODAL_WIDTH;
}

function getOffsetParent(element: HTMLElement) {
	const offsetParent = element.offsetParent;
	if (offsetParent) {
		return offsetParent;
	}

	// If offsetParent is null, we need to find the nearest ancestor element that has a position value other than static
	let tempOffsetParent = element.parentElement;
	while (tempOffsetParent) {
		const position = getComputedStyle(tempOffsetParent).position;
		if (position !== 'static') {
			return tempOffsetParent;
		}
		tempOffsetParent = tempOffsetParent.parentElement;
	}
	return document.body;
}

// Calculate offset for modal positioning
function getModalOffset({
	from,
	modalElement,
	direction,
}: {
	direction: 'top' | 'left';
	from: HTMLElement;
	modalElement: HTMLElement;
}) {
	const property = direction === 'top' ? 'offsetTop' : 'offsetLeft';
	let offset = from[property];
	let tempOffsetParent = from.offsetParent;
	const modalOffsetParent = getOffsetParent(modalElement);
	while (
		tempOffsetParent &&
		tempOffsetParent instanceof HTMLElement &&
		tempOffsetParent !== modalOffsetParent
	) {
		offset += tempOffsetParent[property];
		tempOffsetParent = tempOffsetParent.offsetParent;
	}
	return offset;
}

export function getCenteredAndClampedModalLeft({
	anchorLeft,
	anchorWidth,
	boundsLeft,
	boundsWidth,
	marginLeft,
	modalWidth,
}: {
	anchorLeft: number;
	anchorWidth: number;
	boundsLeft: number;
	boundsWidth: number;
	marginLeft: number;
	modalWidth: number;
}): number {
	const centeredLeft = anchorLeft + (anchorWidth - modalWidth) / 2 - marginLeft;
	const minLeft = boundsLeft - marginLeft;
	const maxLeft = Math.max(minLeft, boundsLeft + boundsWidth - modalWidth - marginLeft);

	return Math.min(Math.max(centeredLeft, minLeft), maxLeft);
}

export function getModalLeft({
	relativePositionElement,
	modalElement,
	appearance,
	positioningBoundsElement,
}: {
	appearance?: EditorAppearance;
	modalElement: HTMLElement;
	positioningBoundsElement?: HTMLElement;
	relativePositionElement: HTMLElement;
}): number | string {
	const offset = getModalOffset({ from: relativePositionElement, modalElement, direction: 'left' });

	if (positioningBoundsElement) {
		const boundsLeft = getModalOffset({
			from: positioningBoundsElement,
			modalElement,
			direction: 'left',
		});
		const marginLeft = Number.parseFloat(getComputedStyle(modalElement).marginLeft) || 0;

		return getCenteredAndClampedModalLeft({
			anchorLeft: offset,
			anchorWidth: relativePositionElement.offsetWidth,
			boundsLeft,
			boundsWidth: positioningBoundsElement.offsetWidth,
			marginLeft,
			modalWidth: modalElement.offsetWidth,
		});
	}

	// Re-centre the narrower modal only when the prompt updates are on and on
	// surfaces that actually use the inset. Gated here (not in the caller) because
	// this helper is shared with the unchanged legacy modal.
	if (isPromptUpdatesEnabledNoExposure() && shouldNarrowAifcModal(appearance)) {
		return `calc(${offset}px + ${MODAL_LEFT_OFFSET_REM}rem)`;
	}

	return offset;
}

/** Reads the rendered deleted-content widgets, optionally restricted to a document range. */
type GetDeletedWidgets = (range?: { from: number; to: number }) => DeletedDiffWidget[];

/**
 * Extend `bottom` past deleted diff content that would otherwise sit behind the modal. With `smart`
 * diffs, deleted content is anchored below its replacement and can be relocated past the mount
 * point. show-diff's `getDeletedWidgets` action returns each deleted widget with the position it is
 * anchored at; only the closest one within the enclosing top-level node is measured, since anything
 * further belongs to another change and must not drag the modal further down the page.
 */
function extendBottomPastDeletedDiff({
	editorView,
	getDeletedWidgets,
	modalDecorationElement,
	modalElement,
	bottom,
}: {
	bottom: number;
	editorView: EditorView;
	getDeletedWidgets: GetDeletedWidgets;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}): number {
	let from: number;
	let to: number;
	try {
		from = editorView.posAtDOM(modalDecorationElement, 0);
		// Relocation only pushes content out of nodes that cannot host it, so it never escapes the
		// enclosing top-level node.
		const $from = editorView.state.doc.resolve(from);
		to = $from.depth > 0 ? $from.after(1) : editorView.state.doc.content.size;
	} catch {
		// posAtDOM/resolve throw when the mount point is not currently in the document.
		return bottom;
	}

	// Widgets come back ordered by position, so the first is the closest to the mount point.
	const [closest] = getDeletedWidgets({ from, to });
	if (!closest) {
		return bottom;
	}

	const elementBottom =
		getModalOffset({ from: closest.element, modalElement, direction: 'top' }) +
		closest.element.offsetHeight;
	return Math.max(bottom, elementBottom);
}

// Calculate offset top for decorationBottom positioning
function getDecorationBottom({
	editorView,
	getDeletedWidgets,
	modalDecorationElement,
	modalElement,
}: {
	editorView?: EditorView;
	getDeletedWidgets?: GetDeletedWidgets;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}) {
	const offset = getModalOffset({ from: modalDecorationElement, modalElement, direction: 'top' });
	const bottom = offset + modalDecorationElement.offsetHeight;

	// Gated on the same flag as the placement change that causes the overlap: deleted content only
	// renders after the mount point when `smart` diffs place deletions below.
	if (!editorView || !getDeletedWidgets || !fg('platform_editor_ai_smart_diff')) {
		return bottom;
	}

	return extendBottomPastDeletedDiff({
		editorView,
		getDeletedWidgets,
		modalDecorationElement,
		modalElement,
		bottom,
	});
}

/**
 * The editor's parent when a max-height makes the comment text scroll.
 * No max-height means a chromeless editor that keeps the old placement.
 */
function getHeightConstrainedContentArea(
	editorDom: Element | null | undefined,
): HTMLElement | null {
	if (!(editorDom instanceof HTMLElement)) {
		return null;
	}

	const contentArea = editorDom.parentElement;
	if (!(contentArea instanceof HTMLElement)) {
		return null;
	}

	if (getComputedStyle(contentArea).maxHeight === 'none') {
		return null;
	}

	return contentArea;
}

export function isInlineCommentPromptContained(
	appearance: EditorAppearance | undefined,
	editorDom: Element | null | undefined,
): boolean {
	return (
		appearance === 'chromeless' &&
		isPromptUpdatesEnabledNoExposure() &&
		getHeightConstrainedContentArea(editorDom) !== null
	);
}

/**
 * The prompt is position: absolute, so its top is measured from the offset parent.
 * position: relative here makes the scrolling comment text that parent, so top is
 * the cursor's distance from the top of the comment. Scrolling moves them together.
 * Restores the previous position when the prompt closes.
 */
export function containInlineCommentPrompt(
	appearance: EditorAppearance | undefined,
	editorDom: Element | null | undefined,
): () => void {
	const contentArea = getHeightConstrainedContentArea(editorDom);
	if (
		appearance !== 'chromeless' ||
		!isPromptUpdatesEnabledNoExposure() ||
		!contentArea ||
		getComputedStyle(contentArea).position !== 'static'
	) {
		return () => {};
	}

	const previousPosition = contentArea.style.position;
	contentArea.style.position = 'relative';

	return () => {
		contentArea.style.position = previousPosition;
	};
}

export function getModalTop({
	appearance,
	modalElement,
	editorRelativeWrapper,
	modalDecorationElement,
	lastTriggeredFrom,
	editorView,
	getDeletedWidgets,
}: {
	appearance?: EditorAppearance;
	editorRelativeWrapper?: Element | null;
	editorView?: EditorView;
	getDeletedWidgets?: GetDeletedWidgets;
	lastTriggeredFrom?: InvokedFrom;
	modalDecorationElement: HTMLElement;
	modalElement: HTMLElement;
}): number {
	const isSuggestTitleQuickAction = lastTriggeredFrom === 'confluenceTitleToolbar';

	if (isSuggestTitleQuickAction) {
		return editorRelativeWrapper?.querySelector<HTMLElement>('.ProseMirror')?.offsetTop || 0;
	}
	const calculatedTop = getDecorationBottom({
		editorView,
		getDeletedWidgets,
		modalDecorationElement,
		modalElement,
	});

	// Prompt updates are on and the comment scrolls, so calculatedTop is already
	// the cursor. Skip the clamp that pins the prompt to the visible bottom.
	if (isInlineCommentPromptContained(appearance, editorView?.dom)) {
		return calculatedTop;
	}

	// Chromeless with prompt updates off, or with no max-height: if top is past
	// the visible bottom of the comment, pull the prompt back to that edge.
	if (appearance === 'chromeless') {
		const editorContentArea = editorView?.dom.closest<HTMLElement>('.ak-editor-content-area');
		if (editorContentArea) {
			const modalOffsetParent = getOffsetParent(modalElement);

			const contentAreaRect = editorContentArea.getBoundingClientRect();
			const modalOffsetParentRect = modalOffsetParent.getBoundingClientRect();

			const contentAreaBottom = contentAreaRect.bottom - modalOffsetParentRect.top;

			if (calculatedTop > contentAreaBottom) {
				return contentAreaBottom;
			}
		}
	}

	return calculatedTop;
}

/**
 * Gets wrapper of the nearest resizable element or the
 * wrapper of the nearest breakout element looking up from the given position
 * this wrapper is to be used as a reference point for ai modal positioning
 */
export function getResizableOrBreakoutWrapper(
	editorView: EditorView,
	position: number,
): HTMLElement | undefined {
	// For in-document streaming, the mount decoration is placed at the end of the document
	// But somehow during streaming, the position can be larger than the document size
	// So we need to clamp the position to the document size
	const resolvedPos = editorView.state.doc.resolve(
		Math.min(position, editorView.state.doc.content.size),
	);
	const { nodes, marks } = editorView.state.schema as Schema;
	const domAtPos = editorView.domAtPos.bind(editorView);

	/**
	 * At the moment, the table node is the only resizable node
	 * which can trigger the AI modal.
	 */
	const resizableNode = findParentNodeClosestToPos(
		resolvedPos,
		(currentNode) => currentNode.type === nodes.table,
	);

	if (resizableNode) {
		const resizableDomNode = findDomRefAtPos(resizableNode.start, domAtPos);

		if (resizableDomNode instanceof HTMLElement) {
			/**
			 * At the moment this only resizable tables need to be handled
			 * hence why we use the table resizer class name specifically.
			 */
			const resizableWrapper = resizableDomNode.closest(
				`.${TableSharedCssClassName.TABLE_RESIZER_CONTAINER}`,
			);
			if (resizableWrapper instanceof HTMLElement) {
				return resizableWrapper;
			}
		}
	}
	const nodeWithBreakout = findParentNodeClosestToPos(resolvedPos, (currentNode) => {
		return (
			(currentNode.type === nodes.table && currentNode.attrs.layout !== 'default') ||
			currentNode.marks.some((mark) => mark.type === marks.breakout)
		);
	});
	if (!nodeWithBreakout) {
		return;
	}
	const domNodeWithBreakout = findDomRefAtPos(nodeWithBreakout.start, domAtPos);
	if (!(domNodeWithBreakout instanceof HTMLElement)) {
		return;
	}
	if (!domNodeWithBreakout) {
		return;
	}
	if ([nodes.layoutSection, nodes.expand].includes(nodeWithBreakout.node.type)) {
		return domNodeWithBreakout.parentElement || undefined;
	}

	if (nodeWithBreakout.node.type === nodes.table) {
		const closest = domNodeWithBreakout.closest(`.${TableSharedCssClassName.TABLE_CONTAINER}`);
		return closest instanceof HTMLElement ? closest : undefined;
	}
}

/** A scroll container whose scroll anchoring was paused, with its original inline value. */
export type PausedScrollAnchor = { element: HTMLElement; previous: string };

/**
 * Every ancestor below the root that can scroll vertically. `findOverflowScrollParent` only
 * matches `overflow: scroll`, so it misses `overflow: auto` containers such as Jira's issue
 * view. Containers are matched whether or not they overflow yet, because a short description
 * can start overflowing mid-stream. The root element is left alone because the product owns it.
 */
export const findScrollContainers = (element: HTMLElement): HTMLElement[] => {
	const containers: HTMLElement[] = [];
	let current = element.parentElement;
	while (current && current !== document.documentElement) {
		const { overflowY } = getComputedStyle(current);
		if (overflowY === 'auto' || overflowY === 'scroll') {
			containers.push(current);
		}
		current = current.parentElement;
	}
	return containers;
};

/**
 * Pause browser scroll anchoring on every scroll container above `element`, so text streamed
 * above the viewport does not scroll the container. Returns what to restore afterwards.
 */
export const pauseScrollAnchoring = (element: HTMLElement): PausedScrollAnchor[] => {
	const paused = findScrollContainers(element).map((container) => ({
		element: container,
		previous: container.style.overflowAnchor,
	}));
	paused.forEach(({ element: container }) => {
		container.style.overflowAnchor = 'none';
	});
	return paused;
};

/** Restore scroll anchoring paused by `pauseScrollAnchoring` and clear the ref. */
export const restoreScrollAnchoring = (
	pausedAnchorRef: MutableRefObject<PausedScrollAnchor[] | null>,
): void => {
	pausedAnchorRef.current?.forEach(({ element, previous }) => {
		element.style.overflowAnchor = previous;
	});
	pausedAnchorRef.current = null;
};
